import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import SignaturePad from './SignaturePad';
import { formatDate } from '../utils/dateUtils';
import { intakeTokenFor } from '../utils/hotelUtils';

const emptyVet = () => ({ vetWeight: '', vetTemperature: '', vetNotes: '', vetMedication: '' });

/**
 * Check-in or check-out for every dog of a reservation on one page: health
 * fields per dog, one "Personal" and one owner signature that cover them all.
 * A dog that is not arriving / leaving with the rest can be unticked and
 * handled on its own later.
 */
const HotelGroupVetForm = ({ mode, bookings, onSubmit, onCancel, loading, onDirtyChange = () => {} }) => {
    const navigate = useNavigate();
    const isCheckout = mode === 'checkout';

    const [included, setIncluded] = useState(() => Object.fromEntries(bookings.map(b => [b._id, true])));
    const [vetByBooking, setVetByBooking] = useState(() => Object.fromEntries(bookings.map(b => [b._id, emptyVet()])));
    const [checkedBy, setCheckedBy] = useState('');
    const [ownerSignature, setOwnerSignature] = useState('');
    const [errors, setErrors] = useState({});

    // Report unsaved-changes state to the parent so it can warn before navigating away
    const initialSnapshotRef = useRef(JSON.stringify({ included, vetByBooking, checkedBy, ownerSignature }));
    useEffect(() => {
        onDirtyChange(JSON.stringify({ included, vetByBooking, checkedBy, ownerSignature }) !== initialSnapshotRef.current);
    }, [included, vetByBooking, checkedBy, ownerSignature]);

    // Clear the parent's dirty flag once this form instance goes away
    useEffect(() => () => onDirtyChange(false), []);

    const first = bookings[0];
    const ownerNames = first?.dog?.owners?.map(o => o.name).join(', ') || '';
    const selectedIds = bookings.filter(b => included[b._id]).map(b => b._id);

    const updateVet = (bookingId, field, value) => {
        setVetByBooking(prev => ({ ...prev, [bookingId]: { ...prev[bookingId], [field]: value } }));
        if (errors[`${bookingId}.${field}`]) setErrors(prev => ({ ...prev, [`${bookingId}.${field}`]: '' }));
    };

    const validate = () => {
        const e = {};
        if (selectedIds.length === 0) e.general = 'Selecciona al menos un perro';
        selectedIds.forEach(id => {
            const vet = vetByBooking[id];
            if (vet.vetWeight && (isNaN(vet.vetWeight) || vet.vetWeight <= 0))
                e[`${id}.vetWeight`] = 'El peso debe ser un número positivo';
            if (vet.vetTemperature && (isNaN(vet.vetTemperature) || vet.vetTemperature < 35 || vet.vetTemperature > 43))
                e[`${id}.vetTemperature`] = 'La temperatura debe estar entre 35°C y 43°C';
        });
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    // Held for the whole save, so a second tap cannot send every dog twice
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting || !validate()) return;
        setSubmitting(true);
        try {
            await onSubmit({
                entries: selectedIds.map(bookingId => ({ bookingId, ...vetByBooking[bookingId] })),
                vetCheckedBy: checkedBy,
                ownerSignature,
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleShareIntakeForm = () => {
        const url = `${window.location.origin}/hotel/intake/${intakeTokenFor(first)}`;
        const ownerPhone = first?.dog?.owners?.[0]?.phone;
        const dogNames = bookings.map(b => b.dog?.name).filter(Boolean).join(', ') || 'tus perros';
        const text = `Hola! Te compartimos el formulario de ingreso de *${dogNames}* al hotel. Por favor completalo antes de su llegada:\n${url}`;
        if (ownerPhone) {
            window.open(`https://wa.me/${ownerPhone.replace(/\D/g, '')}?text=${encodeURIComponent(text)}`, '_blank');
        } else if (navigator.share) {
            navigator.share({ title: 'Formulario de ingreso', url, text }).catch(() => {});
        } else {
            navigator.clipboard?.writeText(url);
            alert('Enlace copiado al portapapeles');
        }
    };

    return (
        <div className="hotel-booking-form">
            <h3>{isCheckout ? 'Registrar Salida de la Reserva' : 'Registrar Entrada de la Reserva'}</h3>
            <p className="form-description">
                {ownerNames && <>{ownerNames} · </>}
                {formatDate(first?.checkIn)} → {formatDate(first?.checkOut)} · {bookings.length} perro{bookings.length !== 1 ? 's' : ''}
            </p>

            <form onSubmit={handleSubmit}>
                {!isCheckout && (
                    <div className="intake-form-block">
                        <div className="intake-form-block-info">
                            <span className="intake-form-block-icon">📋</span>
                            <div>
                                <div className="intake-form-block-title">Formulario de ingreso</div>
                                <div className="intake-form-block-sub">Alimentación, medicamentos, pertenencias y comportamiento de todos los perros</div>
                            </div>
                        </div>
                        <div className="intake-form-block-actions">
                            <button type="button" className="intake-form-open-btn" onClick={() => navigate(`/hotel/intake/${intakeTokenFor(first)}/staff`)}>
                                Abrir formulario
                            </button>
                            <button type="button" className="intake-form-share-btn" onClick={handleShareIntakeForm}>
                                📤 Compartir
                            </button>
                        </div>
                    </div>
                )}

                <div className="hb-health-section">
                    <div className="hb-health-section-header">
                        <span className="hb-health-section-title">
                            🩺 Evaluación de salud {isCheckout ? '(final)' : '(entrada)'}
                        </span>
                        <span className="hb-health-section-sub">
                            {selectedIds.length} de {bookings.length} · Desmarca un perro si no {isCheckout ? 'sale' : 'llega'} hoy
                        </span>
                    </div>

                    {bookings.map(booking => {
                        const dog = booking.dog || {};
                        const vet = vetByBooking[booking._id];
                        const on = !!included[booking._id];
                        return (
                            <div key={booking._id} className={`hb-health-card${on ? '' : ' gv-card--off'}`}>
                                <label className="hb-health-card-header gv-card-header">
                                    <input
                                        type="checkbox"
                                        className="gv-include"
                                        checked={on}
                                        onChange={e => setIncluded(prev => ({ ...prev, [booking._id]: e.target.checked }))}
                                    />
                                    <div className="dog-picker-avatar hb-health-avatar">
                                        {dog.pictureUrl || dog.picture
                                            ? <img src={dog.pictureUrl || dog.picture} alt={dog.name} />
                                            : <span>{dog.name?.[0]?.toUpperCase()}</span>
                                        }
                                    </div>
                                    <div className="hb-health-card-info">
                                        <span className="hb-health-card-name">{dog.name || 'Perro desconocido'}</span>
                                        <span className="hb-health-card-sub">
                                            {on ? dog.breed : `No ${isCheckout ? 'sale' : 'entra'} ahora`}
                                        </span>
                                    </div>
                                </label>

                                {on && (
                                    <div className="hb-health-card-body">
                                        <div className="form-grid">
                                            <div className="form-group">
                                                <label>Peso (kg)</label>
                                                <input
                                                    type="number"
                                                    value={vet.vetWeight}
                                                    onChange={e => updateVet(booking._id, 'vetWeight', e.target.value)}
                                                    step="0.1" min="0.1" max="100"
                                                    placeholder="e.g. 25.5"
                                                />
                                                {errors[`${booking._id}.vetWeight`] && <div className="error">{errors[`${booking._id}.vetWeight`]}</div>}
                                            </div>
                                            <div className="form-group">
                                                <label>Temperatura (°C)</label>
                                                <input
                                                    type="number"
                                                    value={vet.vetTemperature}
                                                    onChange={e => updateVet(booking._id, 'vetTemperature', e.target.value)}
                                                    step="0.1" min="35" max="43"
                                                    placeholder="e.g. 38.5"
                                                />
                                                {errors[`${booking._id}.vetTemperature`] && <div className="error">{errors[`${booking._id}.vetTemperature`]}</div>}
                                            </div>
                                        </div>
                                        <div className="form-group full-width">
                                            <label>Notas de salud</label>
                                            <textarea
                                                value={vet.vetNotes}
                                                onChange={e => updateVet(booking._id, 'vetNotes', e.target.value)}
                                                placeholder={`Observaciones de salud o ${isCheckout ? 'recomendaciones' : 'preocupaciones médicas'}...`}
                                                rows="3"
                                                maxLength="5000"
                                            />
                                        </div>
                                        <div className="form-group full-width">
                                            <label>Medicación</label>
                                            <textarea
                                                value={vet.vetMedication}
                                                onChange={e => updateVet(booking._id, 'vetMedication', e.target.value)}
                                                placeholder="Medicamentos indicados, dosis y frecuencia..."
                                                rows="2"
                                                maxLength="500"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                <div className="vet-section">
                    <div className="form-group">
                        <label>Personal</label>
                        <input
                            type="text"
                            value={checkedBy}
                            onChange={e => setCheckedBy(e.target.value)}
                            placeholder="Miembro del personal que realizó la evaluación"
                            maxLength="100"
                        />
                    </div>
                    <div className="form-group full-width">
                        <label>Firma del propietario <span className="hb-health-section-sub">· una firma para todos los perros marcados</span></label>
                        {ownerSignature ? (
                            <div className="signature-preview">
                                <img src={ownerSignature} alt="Firma del propietario" className="signature-preview-img" />
                                <button type="button" className="signature-clear-btn" onClick={() => setOwnerSignature('')}>
                                    Volver a firmar
                                </button>
                            </div>
                        ) : (
                            <SignaturePad onChange={setOwnerSignature} />
                        )}
                    </div>
                </div>

                {errors.general && <div className="error">{errors.general}</div>}

                <div className="form-actions">
                    <button type="button" onClick={onCancel} className="btn-secondary">Cancelar</button>
                    <button type="submit" className="btn-primary" disabled={loading || submitting || selectedIds.length === 0}>
                        {loading || submitting
                            ? 'Guardando...'
                            : `${isCheckout ? 'Registrar salida' : 'Registrar entrada'} (${selectedIds.length})`}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default HotelGroupVetForm;
